import React, { FC } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useTheme from 'src/hooks/useTheme'
import SemiRoundedButton, { ButtonSizes } from './Button'

interface IThemeToggleProps {
  className?: string
  minimal?: boolean
  textColor?: string
  iconClassName?: string
}

const SunIcon = ({ className = 'w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5' }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    shapeRendering="geometricPrecision"
    className={`transform-gpu ${className}`}
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
)

const MoonIcon = ({ className = 'w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5' }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    shapeRendering="geometricPrecision"
    className={`transform-gpu ${className}`}
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
)

const ThemeToggle: FC<IThemeToggleProps> = ({
  className = '',
  minimal = false,
  textColor = 'text-white',
  iconClassName = 'w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5',
}) => {
  const { theme, toggleTheme } = useTheme()

  const content = (
    <AnimatePresence exitBeforeEnter initial={false}>
      {theme === 'light' ? (
        <motion.span
          key="moon"
          initial={{ opacity: 0, rotate: -30, scale: 0.8 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 30, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center transform-gpu"
          style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        >
          <MoonIcon className={iconClassName} />
        </motion.span>
      ) : (
        <motion.span
          key="sun"
          initial={{ opacity: 0, rotate: 30, scale: 0.8 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: -30, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center transform-gpu"
          style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        >
          <SunIcon className={iconClassName} />
        </motion.span>
      )}
    </AnimatePresence>
  )

  if (minimal) {
    return (
      <button
        onClick={toggleTheme}
        className={`${textColor} transition-opacity duration-200 ease-out rounded-full flex items-center justify-center cursor-pointer transform-gpu ${className}`}
        style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}
        aria-label="Toggle theme"
      >
        {content}
      </button>
    )
  }

  return (
    <SemiRoundedButton
      onClick={toggleTheme}
      isOutline
      isCircle
      size={ButtonSizes.large}
      noSlide
      className={`${className}`}
    >
      {content}
    </SemiRoundedButton>
  )
}

ThemeToggle.defaultProps = {
  className: '',
}

export default ThemeToggle
